/* eslint-disable import/prefer-default-export */
import axios from "axios";

import { refreshToken } from "./login";

import { persistGracePeriodFromApiPayload } from "../Containers/grace-period/utils";
import {
  getMandatoryUserData,
  getUserCredentials,
  getCachedFirebaseToken,
  setCachedFirebaseToken,
  isThirdPartyUser,
} from "../utils/common";
import { API_URL } from "../utils/Constant";

const resolveRequestUrl = (config) => {
  try {
    if (config.baseURL) {
      return new URL(config.url || "", config.baseURL);
    }
    return new URL(config.url || "", window.location.origin);
  } catch (err) {
    return null;
  }
};

const isS3AmazonAwsRequest = (config) => {
  const resolved = resolveRequestUrl(config);

  if (resolved?.hostname) {
    const { hostname } = resolved;
    return hostname === "s3.amazonaws.com" || hostname.endsWith(".s3.amazonaws.com");
  }

  const combinedUrl = `${config.baseURL || ""}${config.url || ""}`;
  return combinedUrl.includes("s3.amazonaws.com");
};

const applyAuthHeader = (config, token) => {
  if (!config.headers) {
    config.headers = {};
  }

  const removeAuthHeader = () => {
    delete config.headers.Authorization;
    delete config.headers.authorization;
    if (config.headers.common) {
      delete config.headers.common.Authorization;
      delete config.headers.common.authorization;
    }
  };

  if (isS3AmazonAwsRequest(config)) {
    removeAuthHeader();
    return;
  }

  // Set third-party header based on user credentials
  // Third-party users access via invitation links and have thirdParty: true in credentials
  config.headers["third-party"] = isThirdPartyUser() ? "true" : "false";

  // Always use the current token. A header left on a retry is the expired token.
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    return;
  }

  // If token is explicitly getting passed in the API call
  const passedToken = config.headers.authorization || config.headers.Authorization || config.headers.common?.Authorization;
  if (passedToken) {
    config.headers.Authorization = passedToken;
  } else {
    removeAuthHeader();
  }
};

let isRefreshing = false;
let failedQueue = [];
let refreshPromise = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Cancel any in-progress token refresh and reject queued requests
 * This should be called when user logs out to prevent unnecessary API calls
 */
export const cancelTokenRefresh = () => {
  if (isRefreshing) {
    // Reject all queued requests with logout error
    processQueue(new Error("User logged out"), null);
    isRefreshing = false;
    refreshPromise = null;
  }
};

export const axiosInterceptor = () => {
  axios.interceptors.request.use((request) => {
    const { user_payload, company_name, company_domain } = getMandatoryUserData();
    const userCredentials = getUserCredentials();

    // Skip adding Authorization header for refresh token endpoint (uses refresh_token instead)
    const isRefreshEndpoint = request.url?.includes("/login/refresh/");

    // Use cached token if available, otherwise get from storage
    let tokenToUse = isRefreshEndpoint ? null : getCachedFirebaseToken();

    // If we have cached token but no user credentials, clear cache (user logged out)
    if (tokenToUse && !userCredentials) {
      setCachedFirebaseToken(null);
      tokenToUse = null;
    }

    // If no cached token, try to get from storage
    if (!tokenToUse && !isRefreshEndpoint && userCredentials?.firebase_id_token) {
      tokenToUse = userCredentials.firebase_id_token;
      setCachedFirebaseToken(tokenToUse); // Cache it for future requests
    }

    // Add Authorization header with Firebase token
    applyAuthHeader(request, tokenToUse);

    /* Replace fields in params or data obj based on API method */
    const key = request.method === "get" ? "params" : "data";
    const disableInterceptor = !!request[key]?.disableInterceptor;

    if (user_payload && company_name && !disableInterceptor) {
      /* If the url directly contains the mandatory fields, remove them
      (they will be added in the params or data obj of the request) */
      const urlArr = request.url.split("?");
      if (urlArr.length > 1 && urlArr[0].length > 0) {
        const queryParams = new URLSearchParams(urlArr[1]);
        queryParams.delete("user_payload");
        queryParams.delete("company_name");
        queryParams.delete("company_domain");
        const newQueryParamsString = queryParams.toString();
        request.url = `${urlArr[0]}?${newQueryParamsString}`;
      }
      if (!request[key]) {
        request[key] = {};
      }
      request[key].user_payload = user_payload;
      // Keep an explicit company_name (for example a selected location).
      if (request[key].company_name == null || request[key].company_name === "") {
        request[key].company_name = company_name;
      }
      request[key].company_domain = company_domain;
    }

    /* disableInterceptor is an internal-only flag; never send it to the backend */
    if (request[key]?.disableInterceptor) {
      delete request[key].disableInterceptor;
    }
    return request;
  }, (error) => {
    /** Navigate to maintenance page if request error */
    window.location.pathname = "/maintenance";
    return Promise.reject(error);
  });

  axios.interceptors.response.use(
    (response) => {
      persistGracePeriodFromApiPayload(response?.data);
      return response;
    },
    async (error) => {
      const statusCode = error.response?.status;
      const errorDetail = error.response?.data?.detail;
      const errorMessage = error.response?.data?.message;
      const isTokenExpired = errorDetail === "Token has expired." || errorMessage === "Token has expired.";
      const loggedIn = !!getMandatoryUserData().user_payload;
      const originalRequest = error.config;

      /** Handle 401 Unauthorized or Token expired message - Token expired or invalid */
      if ((statusCode === 401 || isTokenExpired) && loggedIn && !originalRequest._retry) {
        // Skip refresh for refresh endpoint itself to prevent infinite loops
        const isRefreshEndpoint = originalRequest.url?.includes("/login/refresh/");
        // Skip refresh for logout endpoint - logout should proceed even if token is expired
        const isLogoutEndpoint = originalRequest.url?.includes("/logout/");

        if (isRefreshEndpoint || isLogoutEndpoint) {
          setCachedFirebaseToken(null); // Clear cached token
          // Clear storage and redirect to login
          // const userCredentials = getUserCredentials();
          // if (userCredentials?.rememberMe) {
          //   localStorage.clear();
          // } else {
          //   sessionStorage.clear();
          // }
          sessionStorage.clear();
          window.location.pathname = "/login";
          return Promise.reject(error);
        }

        if (isRefreshing && refreshPromise) {
          // If already refreshing, queue this request and wait for the existing refresh
          return refreshPromise
            .then((token) => {
              applyAuthHeader(originalRequest, token);
              return axios(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        // Create a promise for the refresh that can be shared by queued requests
        refreshPromise = (async () => {
          try {
            // Check if user is still logged in before attempting refresh
            // This prevents refresh if logout happened during the refresh process
            const currentUserCredentials = getUserCredentials();
            if (!currentUserCredentials || !currentUserCredentials.firebase_refresh_token) {
              const logoutError = new Error("User logged out during token refresh");
              processQueue(logoutError, null);
              isRefreshing = false;
              refreshPromise = null;
              throw logoutError;
            }

            const result = await refreshToken();

            if (result.success && result.firebase_id_token) {
              // Update cached token immediately so all new requests use it
              setCachedFirebaseToken(result.firebase_id_token);

              // Process queued requests with the new token
              processQueue(null, result.firebase_id_token);

              // Retry original request with new token
              applyAuthHeader(originalRequest, result.firebase_id_token);
              isRefreshing = false;
              refreshPromise = null;
              return result.firebase_id_token;
            }
            // Refresh failed, clear storage and redirect to login
            setCachedFirebaseToken(null); // Clear cached token
            processQueue(new Error(result.message || "Token refresh failed"), null);
            isRefreshing = false;
            refreshPromise = null;
            // Clear storage
            // const userCredentials = getUserCredentials();
            // if (userCredentials?.rememberMe) {
            //   localStorage.clear();
            // } else {
            //   sessionStorage.clear();
            // }
            sessionStorage.clear();
            window.location.pathname = "/login";
            throw new Error(result.message || "Token refresh failed");
          } catch (refreshError) {
            setCachedFirebaseToken(null); // Clear cached token
            processQueue(refreshError, null);
            isRefreshing = false;
            refreshPromise = null;
            // Clear storage
            // const userCredentials = getUserCredentials();
            // if (userCredentials?.rememberMe) {
            //   localStorage.clear();
            // } else {
            //   sessionStorage.clear();
            // }
            sessionStorage.clear();
            window.location.pathname = "/login";
            throw refreshError;
          }
        })();

        return refreshPromise
          .then((token) => {
            applyAuthHeader(originalRequest, token);
            return axios(originalRequest);
          })
          .catch((refreshError) => Promise.reject(refreshError));
      }

      /** Navigate to auth-failed page if status 403 */
      if (loggedIn && statusCode === 403 && error.config.url === API_URL) { // Only applied to ESV Base url
        window.location.pathname = "/auth-failed";
      }

      /** Navigate to maintenance page if status 500 */
      if (statusCode === 500) {
        window.location.pathname = "/maintenance";
      }

      return Promise.reject(error);
    },
  );
};
